import React from 'react';
import { motion } from 'framer-motion';
import { BriefcaseIcon, ChartBarIcon, BuildingOffice2Icon } from '@heroicons/react/24/outline';

const About: React.FC = () => {
  const highlights = [
    {
      icon: <ChartBarIcon className="h-8 w-8" />,
      title: "~4 Years in Product",
      description: "Owned strategy, roadmap and delivery for a B2B platform (web, iOS, Android): 20+ releases, < 2% post-release defects"
    },
    {
      icon: <BriefcaseIcon className="h-8 w-8" />,
      title: "4 Years Leading Projects",
      description: "Construction and infrastructure projects in Berlin with clients, contractors and authorities, all brought to joint decisions"
    },
    {
      icon: <BuildingOffice2Icon className="h-8 w-8" />,
      title: "Shareholder at Petrotreatment",
      description: "Business & Tech Advisory: bringing process, data and automation thinking into the business"
    }
  ];

  const education = [
    {
      degree: "MBA Business Informatics",
      school: "Metropolia University of Applied Sciences, Finland",
      logo: "/universities/metropolia.png",
      details: "Part-time, remote",
      period: "Sep 2026 – Present",
      color: "from-primary-50 to-blue-50",
      accent: "text-primary-600"
    },
    {
      degree: "M.Sc. Construction and Real Estate Management",
      school: "HTW Berlin – University of Applied Sciences",
      logo: "/universities/htw-berlin.webp",
      details: null,
      period: "Sep 2016 – Sep 2018",
      color: "from-blue-50 to-indigo-50",
      accent: "text-blue-600"
    },
    {
      degree: "B.Sc. Civil Engineering (Structural Engineering)",
      school: "Cairo University",
      logo: "/universities/cairo-university.jpg",
      details: null,
      period: "Sep 2008 – Jan 2014",
      color: "from-indigo-50 to-purple-50",
      accent: "text-indigo-600"
    }
  ];

  return (
    <section id="about" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            About Me
          </h2>
          <div className="w-24 h-1 bg-primary-600 mx-auto rounded-full"></div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="prose prose-lg">
              <p className="text-gray-600 leading-relaxed mb-6">
                I am a Senior Product Manager based in Berlin with nearly four years of ownership of strategy,
                roadmap and execution for a B2B platform (web, iOS, Android) and the data and operations systems
                beneath it. I dig into data myself: I run my own SQL on the production database to find where a
                process breaks, size the cost and decide what to build.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                I have taken AI features from idea to production, where the hard part was accuracy on messy
                real-world input, and solved it with confidence scoring, thresholds, human review and monitoring.
                Before product, I led complex construction and infrastructure projects for four years with many
                stakeholders who wanted different things. Today I also bring that operations background to
                Petrotreatment, where I am a shareholder and advise on business and technology.
              </p>
              <div className="grid grid-cols-2 gap-4 mt-8">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Location</h4>
                  <p className="text-gray-600">Berlin, Germany</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Languages</h4>
                  <p className="text-gray-600">German (fluent), English (fluent), Arabic (native)</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Email</h4>
                  <p className="text-gray-600 break-all">job@ahmedyoussef.tech</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Phone</h4>
                  <p className="text-gray-600">+49 176 34606979</p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            {highlights.map((highlight, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="bg-gray-50 p-6 rounded-lg hover:shadow-lg transition-shadow"
              >
                <div className="flex items-start space-x-4">
                  <div className="text-primary-600 flex-shrink-0">
                    {highlight.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {highlight.title}
                    </h3>
                    <p className="text-gray-600">
                      {highlight.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Education Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mt-20"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Education
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            {education.map((edu, index) => (
              <div key={index} className={`bg-gradient-to-r ${edu.color} p-6 rounded-lg`}>
                <div className="flex items-center mb-4">
                  <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center mr-4 border border-gray-200 flex-shrink-0 overflow-hidden p-1.5">
                    <img src={edu.logo} alt={`${edu.school} logo`} className="max-w-full max-h-full object-contain" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">
                      {edu.degree}
                    </h4>
                    <p className={`${edu.accent} font-medium`}>
                      {edu.school}
                    </p>
                  </div>
                </div>
                <div className="text-sm text-gray-600">
                  {edu.details && <p className="mb-2">{edu.details}</p>}
                  <p><strong>Period:</strong> {edu.period}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
